import type { AttendanceType } from '@src/models/interfaces/attendanceEvent.interface';
import { ATTENDANCE_TYPE } from '@src/models/interfaces/attendanceEvent.interface';
import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type {
  CompletedMarking,
  MarkingConfirmCopy,
  MarkingPortalScheduledTimes,
  MarkingPortalSession,
  MarkingProgress,
} from '@src/models/interfaces/markingPortal.interface';
import {
  buildAttendanceJourneyTimeline,
  getScheduledAtByTypeFromMillis,
  MARKING_SEQUENCE,
} from '@src/models/mappers/attendanceJourneyTimeline';
import { DateUtil } from '@src/packages/utils/dates';

export { MARKING_SEQUENCE } from '@src/models/mappers/attendanceJourneyTimeline';

const PRIMARY_LABEL_BY_MARKING: Record<AttendanceType, string> = {
  [ATTENDANCE_TYPE.SHIFT_START]: 'markingPortal.marking.register',
  [ATTENDANCE_TYPE.BREAK_START]: 'markingPortal.marking.registerBreak',
  [ATTENDANCE_TYPE.BREAK_END]: 'markingPortal.marking.registerBreak',
  [ATTENDANCE_TYPE.SHIFT_END]: 'markingPortal.marking.finishShift',
  [ATTENDANCE_TYPE.SHIFT_CHANGES]: 'markingPortal.marking.register',
};

const CONFIRM_COPY_BY_MARKING: Partial<Record<AttendanceType, MarkingConfirmCopy>> = {
  [ATTENDANCE_TYPE.SHIFT_START]: {
    titleId: 'markingPortal.marking.confirm.attendance.title',
    submitId: 'markingPortal.marking.confirm.attendance.submit',
    successTitleId: 'markingPortal.marking.confirm.attendance.success',
  },
  [ATTENDANCE_TYPE.BREAK_START]: {
    titleId: 'markingPortal.marking.confirm.break.title',
    submitId: 'markingPortal.marking.confirm.break.submit',
    successTitleId: 'markingPortal.marking.confirm.break.success',
  },
  [ATTENDANCE_TYPE.BREAK_END]: {
    titleId: 'markingPortal.marking.confirm.breakEnd.title',
    submitId: 'markingPortal.marking.confirm.breakEnd.submit',
    successTitleId: 'markingPortal.marking.confirm.breakEnd.success',
  },
  [ATTENDANCE_TYPE.SHIFT_END]: {
    titleId: 'markingPortal.marking.confirm.shiftEnd.title',
    submitId: 'markingPortal.marking.confirm.shiftEnd.submit',
    successTitleId: 'markingPortal.marking.confirm.shiftEnd.success',
  },
};

export const getMarkingConfirmCopy = (type: AttendanceType | null): MarkingConfirmCopy | null =>
  type ? CONFIRM_COPY_BY_MARKING[type] ?? null : null;

const EARLY_WARNING_BY_MARKING: Partial<Record<AttendanceType, string>> = {
  [ATTENDANCE_TYPE.SHIFT_START]: 'markingPortal.marking.confirm.warning.earlyAttendance',
  [ATTENDANCE_TYPE.BREAK_START]: 'markingPortal.marking.confirm.warning.earlyBreak',
  [ATTENDANCE_TYPE.BREAK_END]: 'markingPortal.marking.confirm.warning.earlyBreakEnd',
};

const SCHEDULED_AT_BY_MARKING: Partial<
  Record<AttendanceType, keyof MarkingPortalScheduledTimes>
> = {
  [ATTENDANCE_TYPE.SHIFT_START]: 'startAt',
  [ATTENDANCE_TYPE.BREAK_START]: 'breakStartAt',
  [ATTENDANCE_TYPE.BREAK_END]: 'breakEndAt',
};

export const getEarlyMarkingWarningId = (
  type: AttendanceType | null,
  markingAt: number | null,
  scheduledTimes?: MarkingPortalScheduledTimes | null,
): string | null => {
  if (!type || markingAt === null || !scheduledTimes) {
    return null;
  }

  const scheduledKey = SCHEDULED_AT_BY_MARKING[type];
  const warningId = EARLY_WARNING_BY_MARKING[type];
  if (!scheduledKey || !warningId) {
    return null;
  }

  const scheduledAt = scheduledTimes[scheduledKey];
  if (scheduledAt === null || markingAt >= scheduledAt) {
    return null;
  }

  return warningId;
};

export const getNextMarkingType = (
  completedTypes: readonly AttendanceType[],
): AttendanceType | null => {
  const done = new Set(completedTypes);
  return MARKING_SEQUENCE.find(type => !done.has(type)) ?? null;
};

export const buildMarkingProgress = (
  completed: readonly CompletedMarking[],
  timeZone?: string | null,
  scheduledTimes?: MarkingPortalScheduledTimes | null,
): MarkingProgress => {
  const completedMarkings = [...completed];
  const nextMarkingType = getNextMarkingType(completedMarkings.map(mark => mark.type));
  const showFinishShift = nextMarkingType === ATTENDANCE_TYPE.SHIFT_END;
  const showRegister = nextMarkingType !== null && !showFinishShift;
  const journeyStatus: MarkingProgress['journeyStatus'] =
    completedMarkings.length === 0 ? 'notStarted' : 'inProgress';
  const primaryActionLabelId = nextMarkingType
    ? PRIMARY_LABEL_BY_MARKING[nextMarkingType]
    : PRIMARY_LABEL_BY_MARKING[ATTENDANCE_TYPE.SHIFT_END];

  return {
    completedMarkings,
    nextMarkingType,
    journeyStatus,
    primaryActionLabelId,
    showRegister,
    showFinishShift,
    timeline: buildAttendanceJourneyTimeline(
      completedMarkings,
      timeZone,
      getScheduledAtByTypeFromMillis(scheduledTimes),
    ),
  };
};

export const getCompletedMarkingsFromRecord = (
  record: IAttendanceRecord,
): CompletedMarking[] => {
  const actualByMarking: Record<AttendanceType, unknown> = {
    [ATTENDANCE_TYPE.SHIFT_START]: record.actualStartAt,
    [ATTENDANCE_TYPE.BREAK_START]: record.actualBreakStartAt,
    [ATTENDANCE_TYPE.BREAK_END]: record.actualBreakEndAt,
    [ATTENDANCE_TYPE.SHIFT_END]: record.actualEndAt,
    [ATTENDANCE_TYPE.SHIFT_CHANGES]: undefined,
  };

  const completed: CompletedMarking[] = [];
  for (const type of MARKING_SEQUENCE) {
    const millis = DateUtil.getTimestampMs(actualByMarking[type]);
    if (millis === null) {
      break;
    }
    completed.push({ type, at: millis });
  }

  return completed;
};

export const appendMarking = (
  completed: readonly CompletedMarking[],
  type: AttendanceType,
  at: number | null,
): CompletedMarking[] => {
  const expected = getNextMarkingType(completed.map(mark => mark.type));
  if (type !== expected) {
    return [...completed];
  }
  return [...completed, { type, at }];
};

export const withMarkingProgress = (
  base: MarkingPortalSession,
  completed: readonly CompletedMarking[],
  timeZone?: string | null,
): MarkingPortalSession => {
  const progress = buildMarkingProgress(completed, timeZone, base.scheduledTimes);
  return {
    ...base,
    completedMarkings: progress.completedMarkings,
    nextMarkingType: progress.nextMarkingType,
    journeyStatus: progress.journeyStatus,
    primaryActionLabelId: progress.primaryActionLabelId,
    showRegister: progress.showRegister,
    showFinishShift: progress.showFinishShift,
    timeline: progress.timeline,
  };
};

export const withMarkingProgressForPortal = (
  base: MarkingPortalSession,
  completed: readonly CompletedMarking[],
  timeZone?: string | null,
): MarkingPortalSession => {
  const session = withMarkingProgress(base, completed, timeZone);

  if (!base.hasWorkJourney && completed.length === 0) {
    return { ...session, journeyStatus: 'noJourney' };
  }

  return session;
};
